/* Glossário central do Projeto Nostradamus.
 *
 * Fonte única de verdade para siglas, classificações metodológicas e termos
 * usados em toda a aplicação. Consumido por:
 *   - GlossaryTooltip (hover inline em siglas)
 *   - GlossarioPage (busca/categorias)
 *   - ModuleHelp (resumo institucional)
 *
 * Para adicionar um termo: incluir no array `GLOSSARY` com `key` único.
 * `key` é o identificador (sem case-sensitivity) usado por GlossaryTooltip.
 */

export const GLOSSARY_CATEGORIES = [
  { id: "vetores", label: "Vetores LSC", color: "#0F4C8B" },
  { id: "classificacao", label: "Classificação das Sementes", color: "#7A1E3C" },
  { id: "pestel", label: "PESTEL", color: "#5C2D8A" },
  { id: "metodologia", label: "Metodologia", color: "#1B7A8B" },
  { id: "sistema", label: "Sistema", color: "#506B30" },
];

export const GLOSSARY = [
  // ─── Vetores LSC ────────────────────────────────────────────────────
  { key: "GLO", category: "vetores", title: "GLO — Garantia da Lei e da Ordem",
    short: "Garantia da Lei e da Ordem: emprego episódico das Forças Armadas em situações específicas de preservação da ordem pública.",
    full: "Garantia da Lei e da Ordem. Emprego episódico das Forças Armadas em situações específicas, conforme previsão constitucional e legal, para preservação da ordem pública e da segurança." },
  { key: "Fronteiras", category: "vetores", title: "Fronteiras",
    short: "Segurança de fronteiras e combate a ilícitos transnacionais.",
    full: "Vetor que abrange a segurança das fronteiras terrestres, fluviais, marítimas e aéreas, com foco no combate ao contrabando, tráfico transnacional e migração irregular." },
  { key: "Segurança Marítima", category: "vetores", title: "Segurança Marítima",
    short: "Segurança marítima, naval e hidroviária.",
    full: "Vetor de proteção da Amazônia Azul, hidrovias interiores e linhas de comunicação marítima, incluindo combate à pirataria, pesca ilegal e proteção de cabos submarinos." },
  { key: "Inteligência de Defesa", category: "vetores", title: "Inteligência de Defesa",
    short: "Inteligência de Defesa aplicada à Segurança Pública.",
    full: "Atividades de inteligência, contrainteligência e ameaças híbridas com aplicação ao espectro de Segurança Pública e Defesa." },
  { key: "Apoio Interagências", category: "vetores", title: "Apoio Interagências",
    short: "Apoio logístico e operacional a órgãos civis.",
    full: "Atuação coordenada entre Forças Armadas, órgãos de segurança e agências civis em operações conjuntas, emergências ou crises." },
  { key: "Defesa Civil", category: "vetores", title: "Defesa Civil",
    short: "Defesa Civil, eventos extremos e crises internas.",
    full: "Resposta a calamidades, eventos climáticos extremos, desastres naturais e crises internas com mobilização interagências." },
  { key: "Grandes Eventos", category: "vetores", title: "Grandes Eventos",
    short: "Segurança de grandes eventos e proteção de autoridades.",
    full: "Planejamento e execução de segurança para cúpulas internacionais, eventos esportivos, visitas oficiais e proteção de autoridades." },
  { key: "Antiterrorismo", category: "vetores", title: "Antiterrorismo",
    short: "Prevenção e combate ao terrorismo.",
    full: "Prevenção, detecção e neutralização de ameaças terroristas e extremismo violento, incluindo financiamento ilícito e radicalização." },
  { key: "Ilícitos Ambientais", category: "vetores", title: "Ilícitos Ambientais",
    short: "Repressão a ilícitos ambientais.",
    full: "Combate a garimpo ilegal, desmatamento, biopirataria, queimadas criminosas e crimes ambientais em áreas protegidas e fronteiriças." },

  // ─── Classificação Metodológica das Sementes ───────────────────────
  { key: "TP", category: "classificacao", title: "TP — Tendência de Peso",
    short: "Eventos cuja direção e sentido são suficientemente consolidados para admitir continuidade no futuro.",
    full: "Tendência de Peso. Eventos ou processos cuja direção e sentido são suficientemente consolidados para admitir continuidade no futuro." },
  { key: "FPD", category: "classificacao", title: "FPD — Fato Pré-Determinado",
    short: "Evento já conhecido, cuja ocorrência futura é praticamente certa.",
    full: "Fato Pré-Determinado. Evento já conhecido, cuja ocorrência futura é praticamente certa, embora ainda não tenha se materializado completamente." },
  { key: "FPF", category: "classificacao", title: "FPF — Fato Portador de Futuro",
    short: "Sinal pequeno no presente, mas com grande potencial de impacto futuro.",
    full: "Fato Portador de Futuro. Sinal existente no ambiente, pequeno no presente, mas com grandes consequências e potencialidades futuras." },
  { key: "IC", category: "classificacao", title: "IC — Incerteza Crítica",
    short: "Variável incerta e de alta importância para construção de cenários.",
    full: "Incerteza Crítica. Evento ou variável incerta e de alta importância para a construção de cenários, capaz de gerar múltiplos futuros possíveis." },
  { key: "SI", category: "classificacao", title: "SI — Surpresa Inevitável",
    short: "Força já em operação, mas incerta quanto ao momento e consequências.",
    full: "Surpresa Inevitável. Força previsível, já em operação, mas sem clareza sobre quando, como ou com quais consequências irá se configurar." },
  { key: "Wild Card", category: "classificacao", title: "Wild Card",
    short: "Evento raro, difícil de antecipar e de alto impacto.",
    full: "Evento de baixa probabilidade, alto impacto e difícil antecipação, com potencial de rápida materialização." },
  { key: "EA", category: "classificacao", title: "EA — Principais Atores e suas Estratégias",
    short: "Ações de indivíduos, grupos ou organizações capazes de influenciar o sistema analisado.",
    full: "Principais Atores e suas Estratégias. Ações de indivíduos, grupos, organizações ou instituições capazes de influenciar significativamente o sistema analisado." },

  // ─── PESTEL ─────────────────────────────────────────────────────────
  { key: "PESTEL", category: "pestel", title: "PESTEL",
    short: "Análise por dimensões Política, Econômica, Social, Tecnológica, Ambiental e Legal.",
    full: "Matriz de análise que classifica fatores em dimensões Política, Econômica, Social, Tecnológica, Ambiental e Legal." },
  { key: "Política", category: "pestel", title: "Política", short: "Dimensão política do PESTEL.",
    full: "Dimensão referente a políticas públicas, governança, regulação, estabilidade institucional e atores políticos." },
  { key: "Econômica", category: "pestel", title: "Econômica", short: "Dimensão econômica do PESTEL.",
    full: "Dimensão referente a macroeconomia, comércio, mercado, indicadores fiscais e financeiros." },
  { key: "Social", category: "pestel", title: "Social", short: "Dimensão social do PESTEL.",
    full: "Dimensão referente a demografia, cultura, valores, mobilidade social e desigualdades." },
  { key: "Tecnológica", category: "pestel", title: "Tecnológica", short: "Dimensão tecnológica do PESTEL.",
    full: "Dimensão referente a inovações, P&D, novas plataformas, sistemas e capacidades tecnológicas emergentes." },
  { key: "Ambiental", category: "pestel", title: "Ambiental", short: "Dimensão ambiental do PESTEL.",
    full: "Dimensão referente a clima, recursos naturais, biodiversidade, sustentabilidade e externalidades ambientais." },
  { key: "Legal", category: "pestel", title: "Legal", short: "Dimensão legal do PESTEL.",
    full: "Dimensão referente a marcos jurídicos, legislação, normas, regulamentação setorial e direito internacional." },

  // ─── Metodologia ───────────────────────────────────────────────────
  { key: "Fonte", category: "metodologia", title: "Fonte",
    short: "Documento analisado: artigo, relatório, livro, paper, documento governamental, literatura cinzenta.",
    full: "Documento analisado, como artigo científico, relatório institucional, policy paper, livro, documento governamental ou literatura cinzenta." },
  { key: "Insumo", category: "metodologia", title: "Insumo",
    short: "Trecho extraído de uma fonte com sinal prospectivo relevante.",
    full: "Trecho extraído de uma fonte que contém sinal prospectivo relevante, podendo ser parágrafo, conjunto de parágrafos, trecho de seção ou até uma página." },
  { key: "Semente de Futuro", category: "metodologia", title: "Semente de Futuro",
    short: "Resultado analítico derivado de um insumo: tendência, hipótese, driver, incerteza ou sinal.",
    full: "Resultado analítico derivado de um insumo, podendo representar tendência, hipótese prospectiva, driver de mudança, incerteza ou sinal emergente." },
  { key: "Leitura Analítica", category: "metodologia", title: "Leitura Analítica",
    short: "Leitura crítica e qualificada da fonte com foco em sinais prospectivos.",
    full: "Processo de leitura crítica e qualificada da fonte com foco em sinais prospectivos relevantes para a construção de cenários." },
  { key: "Horizonte Temporal", category: "metodologia", title: "Horizonte Temporal",
    short: "Janela futura em que o impacto/sinal se materializa. Mínimo 2030+.",
    full: "Janela futura em que o impacto ou sinal projetado se materializa. No Projeto Nostradamus o mínimo é 2030+." },
  { key: "Citação Direta", category: "metodologia", title: "Citação Direta do Insumo",
    short: "Trecho literal copiado da fonte, sem reformulação.",
    full: "Trecho literal copiado integralmente da fonte original, sem reformulação. Serve como evidência primária da Semente de Futuro." },
  { key: "Revisão Humana", category: "metodologia", title: "Revisão Humana",
    short: "Curadoria metodológica obrigatória antes da aprovação.",
    full: "Etapa obrigatória em que um usuário qualificado valida, corrige ou rejeita os dados sugeridos manualmente ou pela IA." },
  { key: "Enriquecimento por IA", category: "metodologia", title: "Enriquecimento por IA",
    short: "Sugestões automáticas de resumo, classificação, PESTEL, área temática e horizonte.",
    full: "Processo em que a IA sugere resumo, classificação, PESTEL, área temática, horizonte, palavras-chave e justificativa para uma fonte ou insumo." },
  { key: "Base Legada", category: "metodologia", title: "Base Legada",
    short: "Planilha histórica F_SP&D — Base de Literaturas importada para o sistema.",
    full: "Planilha histórica F_SP&D — Base de Literaturas importada para o Projeto Nostradamus. Os registros entram como fontes reais pendentes de enriquecimento e revisão." },
  { key: "Coleta Automática", category: "metodologia", title: "Coleta Automática",
    short: "Busca periódica de fontes abertas relacionadas aos vetores LSC.",
    full: "Módulo destinado à busca periódica de fontes abertas, relatórios, notícias e publicações relacionadas aos vetores do LSC." },
  { key: "Diff Humano × IA", category: "metodologia", title: "Diff Humano × IA",
    short: "Comparação entre sugestão da IA e a versão revisada por humano.",
    full: "Comparação entre o que a IA sugeriu e o que o revisor humano alterou ou aprovou, com destaque das diferenças campo a campo." },
  { key: "Recorte Geográfico", category: "metodologia", title: "Recorte Geográfico",
    short: "Escopo territorial do insumo (Brasil, América do Sul, OTAN, global, etc.).",
    full: "Define o escopo territorial sobre o qual o sinal prospectivo se aplica — pode ser nacional, regional, multilateral ou global." },
  { key: "Link Drive Excepcional", category: "metodologia", title: "Link Drive Excepcional",
    short: "Use só quando o link original/PDF não puder ser anexado.",
    full: "Endereço do Google Drive ou repositório institucional onde a fonte está armazenada. Use apenas em casos excepcionais, quando o link original não estiver disponível e o PDF não puder ser anexado." },
  { key: "Upload com IA", category: "metodologia", title: "Upload com IA",
    short: "Envio de PDF/DOCX/TXT para a IA Gemini sugerir os campos automaticamente.",
    full: "Funcionalidade que utiliza a IA Gemini para ler o documento enviado e sugerir título, fonte, citação direta, classificação, PESTEL, horizonte e demais campos. Sugestões precisam ser revisadas pelo pesquisador." },

  // ─── Sistema ────────────────────────────────────────────────────────
  { key: "Registro Real", category: "sistema", title: "Registro Real",
    short: "Dado de origem verificável (planilha oficial, coleta real, cadastro manual).",
    full: "Dado proveniente de fonte verificável: planilha oficial importada, coleta automática real, cadastro manual com fonte documentada." },
  { key: "Registro Simulado", category: "sistema", title: "Registro Simulado",
    short: "Dado gerado para fins de demonstração ou teste.",
    full: "Dado gerado artificialmente para fins de demonstração, teste ou validação de fluxo. Marcado claramente na interface." },
  { key: "Dados Agregados", category: "sistema", title: "Dados Agregados",
    short: "Indicadores resumidos sem expor registros internos.",
    full: "Indicadores e estatísticas resumidos para a área pública, sem expor dados internos ou identificáveis." },
  { key: "Coleta Real", category: "sistema", title: "Coleta Real",
    short: "Busca em APIs/RSS abertos com dados verificáveis.",
    full: "Coleta em fontes abertas (Crossref, OpenAlex, Semantic Scholar, RSS curado) com dados verificáveis e link original preservado." },
  { key: "Mock", category: "sistema", title: "Mock / Demonstração",
    short: "Conteúdo demonstrativo, não real. Marcado com selo.",
    full: "Conteúdo demonstrativo, não real. Marcado com selo 'Demonstração' na interface pública e nunca usado para validação metodológica." },
  { key: "Auditoria", category: "sistema", title: "Auditoria",
    short: "Trilha de ações relevantes registradas no sistema.",
    full: "Trilha imutável de ações relevantes: login, importação, revisão, aprovação, enriquecimento por IA, alteração de usuários e limpezas." },
  { key: "Mapa Público", category: "sistema", title: "Mapa Público",
    short: "Visualização agregada por país, sem expor registros internos.",
    full: "Mapa da área pública que exibe distribuição por país de forma agregada, sem expor títulos, autores ou identificadores internos." },
  { key: "Área Restrita", category: "sistema", title: "Área Restrita",
    short: "Espaço autenticado para pesquisadores, revisores, coordenadores e administradores.",
    full: "Espaço autenticado do sistema com controle de acesso por perfil. Apenas usuários com status 'ativo' têm acesso." },
];

/** Lookup case-insensitive por key ou title. */
export function findTerm(query) {
  if (!query) return null;
  const q = String(query).trim().toLowerCase();
  return GLOSSARY.find(
    (t) =>
      t.key.toLowerCase() === q ||
      t.title.toLowerCase() === q ||
      t.title.toLowerCase().startsWith(q + " —")
  ) || null;
}

/** Filtro por busca livre (case-insensitive, multi-campo). */
export function searchGlossary(query, categoryId) {
  let items = GLOSSARY;
  if (categoryId && categoryId !== "all") {
    items = items.filter((t) => t.category === categoryId);
  }
  if (query) {
    const q = query.trim().toLowerCase();
    items = items.filter(
      (t) =>
        t.key.toLowerCase().includes(q) ||
        t.title.toLowerCase().includes(q) ||
        t.short.toLowerCase().includes(q) ||
        t.full.toLowerCase().includes(q)
    );
  }
  return items;
}
